import { useState } from "react";
import { Check } from "lucide-react";
import { MIN_REASON_LENGTH } from "@amber/contracts";
import {
  SUGGESTION_RELATIONSHIP_LABELS,
  type FirstPlan,
  type ProfileDetail,
  type SuggestionView,
} from "@amber/profiles/contracts";
import { profileCommands } from "@amber/profiles/ui";
import {
  Button,
  Drawer,
  REASON_TOO_SHORT,
  ReasonField,
  Tag,
  TextArea,
  cx,
  useToast,
  errorText,
  useCommandMutation,
} from "@amber/ui";
import { AFTER_PLAN_CHANGE, ON_PLAN_CONFLICT } from "../queries";
import { PlatformCode } from "./PlatformCode";

type Mode = "idle" | "approve" | "reject";

function decidedText(mode: Mode, suggestion: SuggestionView): string {
  if (mode === "reject") return `${suggestion.title} rejected`;
  return suggestion.collected
    ? `${suggestion.title} approved · Not in any plan`
    : `${suggestion.title} recorded as a known account`;
}

function SuggestionCard({ profileId, suggestion }: { profileId: string; suggestion: SuggestionView }) {
  const toast = useToast();
  const [mode, setMode] = useState<Mode>("idle");
  const [reason, setReason] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const mutation = useCommandMutation({
    run: (csrf) =>
      profileCommands.decide(
        profileId,
        suggestion.id,
        mode === "reject"
          ? { decision: "reject", reason: reason.trim() }
          : {
              decision: "approve",
              ...(reason.trim() ? { reason: reason.trim() } : {}),
            },
        csrf,
      ),
    invalidate: AFTER_PLAN_CHANGE,
    invalidateOnConflict: ON_PLAN_CONFLICT,
    onSuccess: () => {
      toast.show(decidedText(mode, suggestion));
    },
  });
  const rejectError =
    submitted && mode === "reject" && reason.trim().length < MIN_REASON_LENGTH ? REASON_TOO_SHORT : null;
  return (
    <li className="rounded-xl border border-divider px-3 py-2.5">
      <div className="flex items-start gap-2">
        <PlatformCode code={suggestion.platform_code} />
        <span className="min-w-0 flex-1">
          <b className="block truncate font-strong text-fg">{suggestion.title}</b>
          <span className="block text-md text-muted">
            {suggestion.platform_label} · {SUGGESTION_RELATIONSHIP_LABELS[suggestion.relationship]}
          </span>
        </span>
        {!suggestion.collected ? <Tag tone="quiet">Not collected yet</Tag> : null}
      </div>
      <p className="m-0 mt-1.5 text-md">{suggestion.why}</p>
      {suggestion.warning ? <p className="m-0 mt-1 text-md text-waiting">{suggestion.warning}</p> : null}
      {mode === "idle" ? (
        <div className="mt-2 flex gap-1.5">
          <Button
            size="sm"
            onClick={() => {
              setMode("approve");
            }}
          >
            <Check size={12} aria-hidden="true" /> Approve
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setMode("reject");
            }}
          >
            Reject
          </Button>
        </div>
      ) : (
        <form
          noValidate
          className="mt-2"
          onSubmit={(e) => {
            e.preventDefault();
            setSubmitted(true);
            if (mode === "reject" && reason.trim().length < MIN_REASON_LENGTH) return;
            mutation.mutate();
          }}
        >
          {mode === "reject" ? (
            <ReasonField
              value={reason}
              onChange={setReason}
              error={rejectError}
              placeholder="Why this isn't a source for the person"
            />
          ) : (
            <>
              <label
                className="mb-1 block text-xs font-semibold tracking-[0.06em] text-muted uppercase"
                htmlFor={`approve-${suggestion.id}`}
              >
                Reason <span className="font-normal normal-case tracking-normal">optional</span>
              </label>
              <TextArea
                id={`approve-${suggestion.id}`}
                value={reason}
                onChange={(e) => {
                  setReason(e.target.value);
                }}
              />
            </>
          )}
          <div className="mt-2 flex gap-1.5">
            <Button size="sm" variant="primary" type="submit" aria-busy={mutation.isPending || undefined}>
              {mode === "reject" ? "Reject" : "Approve"}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setMode("idle");
                setSubmitted(false);
              }}
            >
              Cancel
            </Button>
          </div>
          {mutation.error ? <p className="m-0 mt-1.5 text-md text-attention">{errorText(mutation.error)}</p> : null}
        </form>
      )}
    </li>
  );
}

export function ReviewSuggestions({
  profile,
  plan,
  onClose,
}: {
  profile: ProfileDetail;
  plan: FirstPlan;
  onClose: () => void;
}) {
  const open = [...plan.preselected, ...plan.suggestions];
  return (
    <Drawer
      title="Suggested sources"
      subtitle={profile.name}
      onClose={onClose}
      footer={
        <span className="flex items-center justify-end gap-2">
          <span className="mr-auto text-md text-muted">
            Approved sources are monitored only once a plan includes them.
          </span>
          <Button size="sm" onClick={onClose}>
            Close
          </Button>
        </span>
      }
    >
      {open.length === 0 ? <p className="m-0 text-md text-muted">No suggestions are waiting for a decision.</p> : null}
      <ul className={cx("m-0 flex list-none flex-col gap-2 p-0 text-ui")}>
        {open.map((s) => (
          <SuggestionCard key={s.id} profileId={profile.id} suggestion={s} />
        ))}
      </ul>
    </Drawer>
  );
}
